import {
  Alert,
  Badge,
  Checkbox,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';

import { describeRegistryPrice, type RegistryProduct } from '@/types/registry-documents';
import { CatalogueErrorAlert } from './CatalogueErrorAlert';
import { NOTES_MAX_LENGTH } from './orderDraft';
import type { OrderDocuments } from './useOrderDocuments';

/** Step 2 — which documents, from the country's catalogue. */
export function DocumentsStep({ documents }: Readonly<{ documents: OrderDocuments }>) {
  const { catalogue } = documents;
  const settled = !catalogue.isLoading && !catalogue.error;
  return (
    <Stack gap="sm" mt="sm">
      {catalogue.isLoading && (
        <Group>
          <Loader size="sm" />
          <Text size="sm">Loading the catalogue…</Text>
        </Group>
      )}
      {catalogue.error && !catalogue.isLoading && (
        <CatalogueErrorAlert error={catalogue.error} onRetry={catalogue.reload} />
      )}
      {settled && catalogue.products.length === 0 && (
        <Alert color="gray" title="Nothing on offer">
          Armadillo lists no documents for this country.
        </Alert>
      )}
      {catalogue.products.length > 0 && (
        <ProductList
          products={catalogue.products}
          selected={documents.selected}
          onChange={documents.setSelected}
        />
      )}
      <Textarea
        label="Notes to Armadillo"
        description="Optional — anything their research desk should know"
        value={documents.notes}
        onChange={(event) => documents.setNotes(event.currentTarget.value)}
        maxLength={NOTES_MAX_LENGTH}
        autosize
        minRows={2}
        data-testid="registry-order-notes"
      />
    </Stack>
  );
}

function ProductList({
  products,
  selected,
  onChange,
}: Readonly<{
  products: RegistryProduct[];
  selected: string[];
  onChange: (codes: string[]) => void;
}>) {
  const toggle = (code: string, ticked: boolean) =>
    onChange(ticked ? [...selected, code] : selected.filter((entry) => entry !== code));
  return (
    <Stack gap={6}>
      {products.map((product) => (
        <ProductRow
          key={product.code}
          product={product}
          checked={selected.includes(product.code)}
          onToggle={(ticked) => toggle(product.code, ticked)}
        />
      ))}
    </Stack>
  );
}

const QUOTE_EXPLANATION =
  'The research team confirms price and availability by email before fulfilling — expect a quote first, not a document';

/** Whether a catalogue turnaround only repeats that the product is on quote. */
function saysQuote(availability: string): boolean {
  return availability.trim().toLowerCase() === 'quote';
}

function ProductRow({
  product,
  checked,
  onToggle,
}: Readonly<{ product: RegistryProduct; checked: boolean; onToggle: (ticked: boolean) => void }>) {
  const price = describeRegistryPrice(product.price, product.currency, product.requiresQuote);
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      align="flex-start"
      data-testid={`registry-product-${product.code}`}
    >
      <Checkbox
        checked={checked}
        onChange={(event) => onToggle(event.currentTarget.checked)}
        label={
          <Stack gap={0}>
            <Text size="sm">{product.description ?? product.code}</Text>
            <Text size="xs" c="dimmed">
              {product.code}
              {product.language ? ` · ${product.language}` : ''}
            </Text>
          </Stack>
        }
      />
      <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
        {product.requiresQuote ? (
          // One "Quote" in the price column carries the explanation; the
          // catalogue repeats the word as the turnaround too, so that line
          // is left out rather than shown three times.
          <Tooltip label={QUOTE_EXPLANATION}>
            <Badge size="sm" color="orange" variant="light" radius="sm">
              {price ?? 'Quote'}
            </Badge>
          </Tooltip>
        ) : (
          <Text size="sm" fw={600}>
            {price ?? '—'}
          </Text>
        )}
        {product.availability && !saysQuote(product.availability) && (
          <Text size="xs" c="dimmed">
            {product.availability}
          </Text>
        )}
      </Stack>
    </Group>
  );
}
